import {
	ActivityListView,
	AnonymizeDialogView,
	AssignmentTimelineView,
	AttributesTableView,
	ConsentPanelView,
	ContactPointListView,
	ConvertDialogView,
	CustomerHeaderView,
	CustomerProfileView,
	NotesTimelineView,
	ReassignDialogView,
} from '@today/ui-domain'
import { type TabEntry, Tabs } from '@today/ui-primitives'
import { LanguageSelector } from '@/components/LanguageSelector'
import { useCustomerDetail } from '@/hooks/customers/useCustomerDetail'
import { useCustomerTabs } from '@/hooks/customers/useCustomerTabs'
import type { CustomerDTO } from '@/services/customer.service'

const profileRows = (c: CustomerDTO, t: (key: string) => string): Array<[string, string]> => [
	['profile.firstName', c.firstName],
	['profile.lastName1', c.lastName1 ?? '—'],
	['profile.lastName2', c.lastName2 ?? '—'],
	['profile.statusPartnerLabel', c.statusPartnerLabel ?? '—'],
	['profile.preferredChannel', c.preferredChannel ? t(`channel.${c.preferredChannel}`) : t('profile.none')],
	['profile.preferredLanguage', c.preferredLocale ?? t('profile.none')],
	['profile.tags', c.tags.join(', ') || '—'],
	['profile.orgUnit', c.orgUnit.name],
	['profile.assignedUser', c.assignedUser.displayName ?? '—'],
	['list.columns.email', c.primaryEmail?.value ?? '—'],
	['list.columns.mobile', c.primaryMobile?.value ?? '—'],
]

type Detail = ReturnType<typeof useCustomerDetail>
type TabsData = ReturnType<typeof useCustomerTabs>

/** Every tab, with whether the viewer may see it (Notes/Attributes need PII, Activity needs audit.read). */
const tabEntries = (detail: Detail, customer: CustomerDTO, tabs: TabsData): Array<TabEntry & { visible: boolean }> => {
	const { t, caps } = detail
	return [
		{
			id: 'profile',
			label: t('tabs.profile'),
			content: <CustomerProfileView t={t} rows={profileRows(customer, t)} />,
			visible: true,
		},
		{
			id: 'contactPoints',
			label: t('tabs.contactPoints'),
			content: <ContactPointListView {...tabs.contactPoints} />,
			visible: true,
		},
		{
			id: 'assignment',
			label: t('tabs.assignment'),
			content: <AssignmentTimelineView t={t} history={tabs.assignments} />,
			visible: true,
		},
		{ id: 'notes', label: t('tabs.notes'), content: <NotesTimelineView {...tabs.notes} />, visible: caps.piiRead },
		{ id: 'consents', label: t('tabs.consents'), content: <ConsentPanelView {...tabs.consents} />, visible: true },
		{
			id: 'attributes',
			label: t('tabs.attributes'),
			content: <AttributesTableView t={t} attributes={tabs.attributes} />,
			visible: caps.piiRead,
		},
		{
			id: 'activity',
			label: t('tabs.activity'),
			content: <ActivityListView t={t} events={tabs.activity} />,
			visible: caps.activity,
		},
	]
}

/** `/customers/:id` — header, tabs (URL `?tab=`) and the reassign/convert/anonymise dialogs (TO-418 customer-ui REQ-004). */
export function CustomerDetailPage() {
	const detail = useCustomerDetail()
	const tabs = useCustomerTabs(detail.id, detail.tab, detail.caps, detail.customer)
	const { t, customer } = detail
	if (detail.notFound) {
		return (
			<p role="alert" className="p-6">
				{t('detail.notFound')}
			</p>
		)
	}
	if (!customer || !detail.header) {
		return (
			<p aria-live="polite" className="p-6">
				{t('detail.loading')}
			</p>
		)
	}
	return (
		<div className="flex flex-col gap-4 p-6">
			<LanguageSelector />
			<CustomerHeaderView {...detail.header} />
			{tabs.toast ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{tabs.toast}
				</p>
			) : null}
			<Tabs
				tabs={tabEntries(detail, customer, tabs).filter((entry) => entry.visible)}
				selectedKey={detail.tab}
				onSelectionChange={(key) => detail.onTabChange(String(key))}
			/>
			<ReassignDialogView {...detail.reassignDialog} />
			<ConvertDialogView {...detail.convertDialog} />
			<AnonymizeDialogView {...detail.anonymizeDialog} />
		</div>
	)
}
